import { useEffect, useRef, useState } from 'react';
import { mosaicPanels, rotateOffset } from '../../utils/mosaic';
import './targetFrame.scss';

const ALADIN_CSS = 'https://aladin.cds.unistra.fr/AladinLite/api/v3/latest/aladin.css';
const ALADIN_JS = 'https://aladin.cds.unistra.fr/AladinLite/api/v3/latest/aladin.js';

// Loads the Aladin Lite widget once (real DSS sky imagery, from the CDS/
// Strasbourg astronomical data center) and caches the ready promise, so
// re-mounting the tool doesn't re-fetch the library.
let aladinReadyPromise = null;
const loadAladin = () => {
    if (aladinReadyPromise) return aladinReadyPromise;
    aladinReadyPromise = new Promise((resolve, reject) => {
        if (!document.querySelector(`link[href="${ALADIN_CSS}"]`)) {
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = ALADIN_CSS;
            document.head.appendChild(link);
        }
        const existing = document.querySelector(`script[src="${ALADIN_JS}"]`);
        const onReady = () => window.A.init.then(resolve).catch(reject);
        if (existing) {
            if (window.A) onReady(); else existing.addEventListener('load', onReady);
            return;
        }
        const script = document.createElement('script');
        script.src = ALADIN_JS;
        script.async = true;
        script.onload = onReady;
        script.onerror = reject;
        document.head.appendChild(script);
    });
    return aladinReadyPromise;
};

// Frame the view a bit wider than the sensor FOV so there's context around
// the box, with floors/ceilings so a huge lens or a tiny high-mag setup both
// still render something sensible (and stay inside DSS's usable range).
const viewFovFor = (fovWidthDeg, fovHeightDeg) => {
    const wide = 2.2 * Math.max(fovWidthDeg, fovHeightDeg);
    return Math.min(50, Math.max(0.05, wide));
};

// Overall width/height of the full mosaic grid (outer edge to outer edge),
// so the view can be zoomed to fit every panel instead of just the one at
// the center.
const mosaicSpanFor = (fovWidthDeg, fovHeightDeg, rows, cols, overlapPct) => {
    const overlapFrac = overlapPct / 100;
    const stepX = fovWidthDeg * (1 - overlapFrac);
    const stepY = fovHeightDeg * (1 - overlapFrac);
    return {
        spanW: fovWidthDeg + (cols - 1) * stepX,
        spanH: fovHeightDeg + (rows - 1) * stepY
    };
};

// Sensor corners in a small flat tangent-plane approximation (fine for the
// sub-tens-of-degrees fields this tool deals with), rotated by the camera
// angle then projected back to ra/dec around the given center.
const rotatedBoxCorners = (ra, dec, fovWidthDeg, fovHeightDeg, rotationDeg) => {
    const cosDec = Math.cos((dec * Math.PI) / 180) || 1;
    const halfW = fovWidthDeg / 2;
    const halfH = fovHeightDeg / 2;
    const local = [[halfW, halfH], [-halfW, halfH], [-halfW, -halfH], [halfW, -halfH]];
    return local.map(([dx, dy]) => {
        const [rx, ry] = rotateOffset(dx, dy, rotationDeg);
        return [ra + rx / cosDec, dec + ry];
    });
};

const TargetFrame = ({ target, fovWidthDeg, fovHeightDeg, rotationDeg = 0, mosaicRows = 1, mosaicCols = 1, mosaicOverlapPct = 10 }) => {
    const containerRef = useRef(null);
    const aladinRef = useRef(null);
    const overlayRef = useRef(null);
    const lastDrawnRef = useRef(null);
    const [ready, setReady] = useState(false);
    const [error, setError] = useState(false);
    const [brightness, setBrightness] = useState(100);

    useEffect(() => {
        if (!target) return;
        let cancelled = false;
        loadAladin()
            .then(() => { if (!cancelled) setReady(true); })
            .catch(() => { if (!cancelled) setError(true); });
        return () => { cancelled = true; };
    }, [target]);

    const drawBox = (ra, dec) => {
        const A = window.A;
        try {
            if (!overlayRef.current) {
                overlayRef.current = A.graphicOverlay({ color: '#E9A63D', lineWidth: 2 });
                aladinRef.current.addOverlay(overlayRef.current);
            }
            overlayRef.current.removeAll();
            if (mosaicRows > 1 || mosaicCols > 1) {
                const panels = mosaicPanels({
                    raCenter: ra,
                    decCenter: dec,
                    fovWidthDeg,
                    fovHeightDeg,
                    rotationDeg,
                    rows: mosaicRows,
                    cols: mosaicCols,
                    overlapFrac: mosaicOverlapPct / 100
                });
                panels.forEach((p) => {
                    overlayRef.current.add(A.polygon(rotatedBoxCorners(p.ra, p.dec, fovWidthDeg, fovHeightDeg, rotationDeg)));
                });
            } else {
                overlayRef.current.add(A.polygon(rotatedBoxCorners(ra, dec, fovWidthDeg, fovHeightDeg, rotationDeg)));
            }
            lastDrawnRef.current = { ra, dec };
        } catch (overlayError) {
            // Aladin Lite's overlay API has thrown internally on rapid
            // re-renders (dev-mode double-effect included) even though
            // the view itself renders correctly; the box just won't draw.
            // eslint-disable-next-line no-console
            console.warn('FOV overlay could not be drawn', overlayError);
        }
    };

    // Creates the view and recenters/zooms it - only when a new target is
    // picked (or the widget first becomes ready), never on every render, so
    // exploring around doesn't get yanked back to the search result.
    useEffect(() => {
        if (!ready || !target || !fovWidthDeg || !fovHeightDeg || !containerRef.current) return;
        const A = window.A;
        const viewFov = viewFovFor(fovWidthDeg, fovHeightDeg);

        if (!aladinRef.current) {
            aladinRef.current = A.aladin(containerRef.current, {
                target: `${target.ra} ${target.dec}`,
                fov: viewFov,
                survey: 'P/DSS2/color',
                cooFrame: 'equatorial',
                showFullscreenControl: false,
                showLayersControl: false,
                showGotoControl: false,
                showCooGridControl: false,
                showSimbadPointerControl: false,
                showZoomControl: true,
                showFrame: false,
                showProjectionControl: false,
                showReticle: false
            });
        } else {
            aladinRef.current.gotoRaDec(target.ra, target.dec);
            aladinRef.current.setFoV(viewFov);
        }
        drawBox(target.ra, target.dec);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ready, target]);

    // Redraws the box in place when only the FOV size or rotation changes
    // (e.g. swapping gear), without touching the view's position or zoom.
    useEffect(() => {
        if (!ready || !aladinRef.current || !fovWidthDeg || !fovHeightDeg) return;
        const center = lastDrawnRef.current || target;
        if (center) drawBox(center.ra, center.dec);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ready, fovWidthDeg, fovHeightDeg, rotationDeg, mosaicRows, mosaicCols, mosaicOverlapPct]);

    // Zooms out to fit the whole mosaic grid whenever it's turned on or its
    // rows/columns/overlap change, so you're not stuck manually zooming out
    // to see panels that no longer fit the view. Only touches zoom, not
    // position, and only while an actual mosaic (more than one panel) is set.
    useEffect(() => {
        if (!ready || !aladinRef.current || !fovWidthDeg || !fovHeightDeg) return;
        if (mosaicRows <= 1 && mosaicCols <= 1) return;
        const { spanW, spanH } = mosaicSpanFor(fovWidthDeg, fovHeightDeg, mosaicRows, mosaicCols, mosaicOverlapPct);
        aladinRef.current.setFoV(viewFovFor(spanW, spanH));
    }, [ready, fovWidthDeg, fovHeightDeg, mosaicRows, mosaicCols, mosaicOverlapPct]);

    // The box always sits at the center of whatever's currently on screen, so
    // redraw it there whenever the view moves (from our own drag handling
    // below, or from scroll-to-zoom, which we don't intercept).
    useEffect(() => {
        if (!ready || !aladinRef.current || !fovWidthDeg || !fovHeightDeg) return;
        const aladin = aladinRef.current;
        let rafId;

        const tick = () => {
            const cur = aladin.getRaDec();
            const last = lastDrawnRef.current;
            if (cur && (!last || Math.abs(cur[0] - last.ra) > 1e-6 || Math.abs(cur[1] - last.dec) > 1e-6)) {
                drawBox(cur[0], cur[1]);
            }
            rafId = requestAnimationFrame(tick);
        };
        rafId = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(rafId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ready, fovWidthDeg, fovHeightDeg, rotationDeg, mosaicRows, mosaicCols, mosaicOverlapPct]);

    // Drag-to-pan, driven ourselves rather than Aladin's own click-drag
    // handler: "content follows the pointer" (drag right reveals what was to
    // the left), the standard touch/map-drag convention. Listeners run in
    // the CAPTURE phase and stop propagation so Aladin's own canvas handler
    // never also tries to pan the same gesture.
    useEffect(() => {
        if (!ready || !aladinRef.current || !containerRef.current) return;
        const aladin = aladinRef.current;
        const container = containerRef.current;
        const dragging = { active: false, x: 0, y: 0 };

        const clientPos = (e) => {
            const point = e.touches ? (e.touches[0] || e.changedTouches[0]) : e;
            return { x: point.clientX, y: point.clientY };
        };

        const onContextMenu = (e) => { e.preventDefault(); e.stopPropagation(); };

        const onDown = (e) => {
            if (typeof e.button === 'number' && e.button !== 0) {
                e.preventDefault();
                e.stopPropagation();
                return;
            }
            if (e.target.closest('.aladin-widgets-toolbar, .aladin-status-bar, .aladin-zoom-in, .aladin-zoom-out, .aladin-btn')) {
                return;
            }
            dragging.active = true;
            const p = clientPos(e);
            dragging.x = p.x;
            dragging.y = p.y;
            e.preventDefault();
            e.stopPropagation();
        };
        const onMove = (e) => {
            if (!dragging.active) return;
            e.preventDefault();
            const p = clientPos(e);
            const dx = p.x - dragging.x;
            const dy = p.y - dragging.y;
            dragging.x = p.x;
            dragging.y = p.y;
            if (!dx && !dy) return;

            const rect = container.getBoundingClientRect();
            const cx = rect.width / 2;
            const cy = rect.height / 2;
            // Telescope-slew convention (matches ASIAIR/scope-control apps):
            // dragging right slews the view rightward, so the displayed sky
            // shifts left, not "grab the photo and drag it" (the opposite,
            // touch-scroll convention this had before).
            const world = aladin.pix2world(cx + dx, cy + dy);
            if (world) aladin.gotoRaDec(world[0], world[1]);
        };
        const onUp = () => { dragging.active = false; };

        container.addEventListener('mousedown', onDown, true);
        container.addEventListener('touchstart', onDown, { capture: true, passive: false });
        container.addEventListener('contextmenu', onContextMenu, true);
        window.addEventListener('mousemove', onMove, { passive: false });
        window.addEventListener('touchmove', onMove, { passive: false });
        window.addEventListener('mouseup', onUp);
        window.addEventListener('touchend', onUp);

        return () => {
            container.removeEventListener('mousedown', onDown, true);
            container.removeEventListener('touchstart', onDown, true);
            container.removeEventListener('contextmenu', onContextMenu, true);
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('touchmove', onMove);
            window.removeEventListener('mouseup', onUp);
            window.removeEventListener('touchend', onUp);
        };
    }, [ready]);

    if (!target) {
        return (
            <div className="target-frame target-frame--empty">
                <p>Search for a target above to see it framed with your equipment.</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="target-frame target-frame--empty">
                <p>Could not load the sky image. Try reloading the page.</p>
            </div>
        );
    }

    if (!fovWidthDeg || !fovHeightDeg) {
        return (
            <div className="target-frame target-frame--empty">
                <p>Enter valid equipment numbers above to see the framing.</p>
            </div>
        );
    }

    return (
        <div className="target-frame">
            <div
                className="target-frame-aladin"
                ref={containerRef}
                style={{ filter: `brightness(${brightness}%)` }}
            />
            {!ready ? <p className="target-frame-loading">Loading sky image&hellip;</p> : null}
            <div className="target-frame-brightness">
                <label className="fov-field-label" htmlFor="target-frame-brightness-input">
                    Brightness ({brightness}%)
                </label>
                <input
                    id="target-frame-brightness-input"
                    className="target-frame-brightness-slider"
                    type="range"
                    min="100"
                    max="400"
                    step="10"
                    value={brightness}
                    onChange={(e) => setBrightness(Number(e.target.value))}
                />
            </div>
            <p className="target-frame-caption">
                Drag to explore, scroll to zoom - {mosaicRows > 1 || mosaicCols > 1
                    ? `the ${mosaicRows}×${mosaicCols} mosaic is always centered on the screen.`
                    : 'the amber box always shows your sensor’s field of view at the center of the screen.'}
            </p>
        </div>
    );
};

export default TargetFrame;
